import {Middleware} from 'redux';

import {globalWindow} from '../../libs/shared/helpers/src/browser/browserHelpers';
import {TaskSourcesEnum} from '../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {AppAction} from '../store/appActions';
import {getCompanyUrl} from '../store/data/company/companySelectors';
import {CellsPanelActionsEnum} from '../store/panels/cells/cellsPanelActions';
import {postAsync} from './http/http';

export const DEFAULT_BUFFER_TIME_MS = 30 * 1000;
export const DEFAULT_INTERNAL_HASHSET_FLUSH_MS = DEFAULT_BUFFER_TIME_MS * 100;

let discrepantConversationIdsBuffer: Array<number> = [];
let conversationIdsSeenSet: Set<number> = new Set();
let companyUrl: string | undefined;
let flushBufferTimeout: ReturnType<typeof setTimeout> | undefined;
let flushSeenSetTimeout: ReturnType<typeof setTimeout> | undefined;

function flushSeenSet() {
  conversationIdsSeenSet = new Set();
  flushSeenSetTimeout = undefined;
}

function setupFlushSeenSetTimeout() {
  if (flushSeenSetTimeout) {
    return;
  }

  flushSeenSetTimeout = setTimeout(flushSeenSet, DEFAULT_INTERNAL_HASHSET_FLUSH_MS);
}

function clearDiscrepantConversationIdsBufferAndTimeout() {
  discrepantConversationIdsBuffer = [];
  flushBufferTimeout = undefined;
}

function flushBuffer() {
  if (!companyUrl) {
    clearDiscrepantConversationIdsBufferAndTimeout();
    return;
  }

  const reindexEndpoint = `${companyUrl}/conversation_batch/reindex`;

  globalWindow.requestInstrumentedIdleCallback(
    () => {
      postAsync(reindexEndpoint, {conversation_ids: discrepantConversationIdsBuffer});
      // Whether the endpoint succeeds or not, flush the buffer.
      clearDiscrepantConversationIdsBufferAndTimeout();
    },
    {source: TaskSourcesEnum.LOGGING},
  );
}

function setupFlushBufferTimeout() {
  // If a timeout is already set, nothing to do
  if (flushBufferTimeout) {
    return;
  }

  // If a timeout is not set, this means one of two things:
  // 1. We've never received discrepant conversation id yet for the life of this
  // session thus far, OR
  // 2. We've flushed the previous buffer
  flushBufferTimeout = setTimeout(flushBuffer, DEFAULT_BUFFER_TIME_MS);
}

function enqueueConversationIdForInvalidConversationTracking(conversationId: number) {
  if (conversationIdsSeenSet.has(conversationId)) {
    return;
  }

  conversationIdsSeenSet.add(conversationId);
  discrepantConversationIdsBuffer.push(conversationId);

  setupFlushBufferTimeout();
  setupFlushSeenSetTimeout();
}

export const InvalidConversationTrackerMiddleware: Middleware =
  ({getState}) =>
  (next) =>
  (action: AppAction) => {
    if (action.type === CellsPanelActionsEnum.P2_DISCREPANCIES_OBSERVED) {
      const {discrepantConversationIds} = action.payload;

      if (discrepantConversationIds.length === 0) {
        return next(action);
      }

      const newCompanyUrl = getCompanyUrl(getState());

      if (companyUrl !== newCompanyUrl) {
        companyUrl = newCompanyUrl;
      }

      discrepantConversationIds.forEach((conversationId: number) =>
        enqueueConversationIdForInvalidConversationTracking(conversationId),
      );
    }

    return next(action);
  };
